import { parse, HTMLElement } from 'node-html-parser';
import { Disclaimer } from '../types/disclaimer.model';

/**
 * This method is used to generate the disclaimer text based on the disclaimer object
 * for local disclaimers. It will also sort the disclaimers based on the rank and merge
 * all disclaimer text into a single string.
 * @param disclaimers array of local disclaimers
 * @returns disclaimer text as a string
 */
export function generateLocalDisclaimerText(disclaimers: Disclaimer[]) {
  return (
    disclaimers
      // Using similar logic from ssg lambda global disclaimer
      .map((disclaimer) => {
        const disclaimerHTML = parse(disclaimer.text);
        // If the data-disclaimer-code element does not have child p tags,
        // then it is invalid and should be removed
        if (
          disclaimerHTML.querySelector('[data-disclaimer-code]') &&
          disclaimerHTML.querySelector('[data-disclaimer-code] p') === null
        ) {
          return {
            ...disclaimer,
            text: '',
          };
        }

        // Get the actual disclaimer code from disclaimer.code (remove everything after --- if any)
        let disclaimerCode = disclaimer.code.split('---')[0];

        // look for match with code audience_country_language_CODE
        const disclaimerCodeGroups = disclaimerCode.match(
          /(.[^_]*)_(.[^_]*)_(.[^_]*)_(.*)/,
        );
        disclaimerCode =
          disclaimerCodeGroups?.length === 5
            ? disclaimerCodeGroups[4]
            : disclaimerCode;

        return {
          ...disclaimer,
          code: disclaimerCode,
          text: getDisclaimerHTMLWithCode(disclaimerHTML, disclaimerCode),
        };
      })
      .filter(
        (disclaimer) => disclaimer.text !== '' && disclaimer.footer !== true,
      )
      .sort((a, b) => a.rank - b.rank)
      .reduce((acc, disclaimer) => {
        return acc + disclaimer.text;
      }, '')
  );
}

/**
 * This method is brought over from ssg lambda global disclaimer
 * It is used to parse the disclaimer text coming from service and insert a span
 * tag in the first paragraph element that uses the disclaimer code as an attribute. Remaining
 * paragraphs are also returned after the first paragraph.
 */
export function getDisclaimerHTMLWithCode(
  disclaimerHTML: HTMLElement,
  disclaimerCode: string,
) {
  const disclaimerFirstParagraph = disclaimerHTML.querySelector('p');
  const disclaimerParagraphList = Array.from(
    disclaimerHTML.querySelectorAll('p'),
  );
  const disclaimerRestParagraphList =
    disclaimerParagraphList.length > 1 ? disclaimerParagraphList.slice(1) : [];
  const disclaimerRestParagraph =
    disclaimerRestParagraphList.length > 0
      ? disclaimerRestParagraphList
          .map((paragraph: HTMLElement) => paragraph.outerHTML)
          .join('')
      : null;

  return `
    <p><span data-disclaimer-code="${disclaimerCode}"></span>${disclaimerFirstParagraph?.innerHTML}</p>
    ${disclaimerRestParagraph ?? ''}
  `.trim();
}
